import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MoreHorizontal, Pencil } from "lucide-react";
import { currentIntlLocale } from "../../lib/i18n";
import { cn } from "@v2/lib/utils";
import { Badge } from "@v2/components/ui/badge";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@v2/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@v2/components/ui/dropdown-menu";
import { supabaseV2 } from "../../lib/supabase";
import { statusBadgeClass, statusLabel } from "../../lib/labels";
import { calculateMonthlySquadScoreDetailed, calculatePeriodSquadScore, isCurrentMonth } from "../../lib/squad-score";
import { seasonReferenceNow, useSeason } from "../../lib/seasons";
import { SeasonScopeToggle } from "../../components/shell/SeasonSwitcher";
import { usePermissions } from "../../lib/permissions";
import { useWorkspaceData } from "../../lib/workspace-data";
import { buildPlayerEaCandidates } from "../../lib/ea-name";
import { ourClubIdOf } from "../../lib/ea-game";
import { Sparkline } from "../../components/charts";
import { EmptyState, SectionLoading } from "../../components/state";
import { FcIcon } from "../../components/brand";
import type { MatchPlayerStat, Player, SessionResponse, SquadScoreSetting } from "../../types";
import { isOutOfSquad, playerStatusLabel } from "../../lib/roster";
import { LinkUserCard } from "./LinkUserCard";
import { PlayerEditDialog } from "./PlayerEditDialog";
import { PlayerEaTab } from "./PlayerEaTab";

export function PlayerDetailView({
  teamId,
  playerId,
  onBack,
}: {
  teamId: string;
  playerId: string | null;
  onBack: () => void;
}) {
  const { t } = useTranslation("players");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { canManage } = usePermissions();
  const [allSeasons, setAllSeasons] = useState(false);
  const { seasons, selectedSeason } = useSeason();
  const { players, attendance: responses, matchStats, events, isLoading: isLoadingScoped } = useWorkspaceData(teamId, { allSeasons });
  // Tabella "Stagioni": serve sempre lo storico completo, diviso per stagione.
  const { matchStats: allMatchStats, isLoading: isLoadingAll } = useWorkspaceData(teamId, { allSeasons: true });
  const isLoading = isLoadingScoped || isLoadingAll;
  // Permette di aprire la scheda di un altro giocatore dai sub-pannelli (es.
  // PlayerRoleComparison → tap su un compagno). Riusiamo lo stesso path /players/<id>
  // canonico delle altre viste.
  const openPlayer = (id: string) => navigate(`/players/${id}`);

  const setStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabaseV2.from("players").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
    },
    onError: (e: any) => alert(t("detail.updateError", { message: e.message || String(e) })),
  });

  const removePlayer = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabaseV2.from("players").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      onBack();
    },
    onError: (e: any) => alert(t("detail.deleteError", { message: e.message || String(e) })),
  });

  const [editOpen, setEditOpen] = useState(false);

  const linkedEmailQuery = useQuery({
    queryKey: ["player-linked-email", playerId],
    queryFn: async () => {
      if (!playerId) return null;
      const { data, error } = await supabaseV2.rpc("get_linked_user_email", { _player_id: playerId });
      if (error) throw error;
      return (data as string) || null;
    },
    enabled: !!playerId,
  });

  const linkUser = useMutation({
    mutationFn: async ({ id, email }: { id: string; email: string }) => {
      const { error } = await supabaseV2.rpc("link_player_to_user_by_email", {
        _player_id: id,
        _email: email,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      queryClient.invalidateQueries({ queryKey: ["player-linked-email", playerId] });
      queryClient.invalidateQueries({ queryKey: ["linkable-members", teamId] });
      queryClient.invalidateQueries({ queryKey: ["unlinked-members", teamId] });
    },
    onError: (e: any) => alert(t("detail.updateError", { message: e.message || String(e) })),
  });

  const unlinkUser = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabaseV2.rpc("unlink_player_user", { _player_id: id });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      queryClient.invalidateQueries({ queryKey: ["player-linked-email", playerId] });
      queryClient.invalidateQueries({ queryKey: ["linkable-members", teamId] });
      queryClient.invalidateQueries({ queryKey: ["unlinked-members", teamId] });
    },
    onError: (e: any) => alert(t("detail.updateError", { message: e.message || String(e) })),
  });
  const activeSettingQuery = useQuery({
    queryKey: ["squad-score-settings", teamId, "active"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("squad_score_settings")
        .select("*")
        .eq("team_id", teamId)
        .eq("is_active", true)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data || null) as SquadScoreSetting | null;
    },
    staleTime: 60_000,
  });
  const activeSetting = activeSettingQuery.data;
  const player = players.find((p) => p.id === playerId);

  // Etichetta visibile del giocatore. Fallback in cascata necessario perché
  // `display_name` è una colonna generata da trim(first_name||' '||last_name):
  // per i provinanti creati col solo gamertag e' vuota.
  const playerDisplayLabel = player
    ? (player.display_name && player.display_name.trim()) ||
      player.ea_account_handle ||
      player.ea_sports_id ||
      "-"
    : "-";

  const playerResponses = useMemo(() => {
    return responses
      .filter((r) => r.player_id === playerId)
      .sort((a, b) => String(b.session_starts_at || "").localeCompare(String(a.session_starts_at || "")));
  }, [playerId, responses]);

  const playerMatchStats = useMemo(() => {
    return matchStats
      .filter((s) => s.player_id === playerId)
      .sort((a, b) => String(b.match_starts_at || "").localeCompare(String(a.match_starts_at || "")));
  }, [playerId, matchStats]);

  if (isLoading) return <SectionLoading />;
  if (!player) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-6 space-y-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft />
            {t("detail.back")}
          </Button>
          <EmptyState text={t("detail.notFound")} />
        </CardContent>
      </Card>
    );
  }

  const goals = playerMatchStats.reduce((acc, s) => acc + (s.goals || 0), 0);
  const assists = playerMatchStats.reduce((acc, s) => acc + (s.assists || 0), 0);
  const starts = playerMatchStats.filter((s) => s.started).length;
  const present = playerResponses.filter((r) => r.status === "present").length;
  const late = playerResponses.filter((r) => r.status === "late").length;
  const absent = playerResponses.filter((r) => r.status === "absent").length;
  const noResponse = playerResponses.filter((r) => r.status === "no_response").length;
  const totalEvents = playerResponses.length;
  const sessionsInCurrentMonth = events.filter((e) => isCurrentMonth(e.starts_at)).length;
  // Stagione in corso: Squad Score del mese. Stagione chiusa o tutte le
  // stagioni: Squad Score sull'intero periodo mostrato.
  const wholePeriodScore = allSeasons || selectedSeason?.status === "closed";
  const latestScoreBreakdown = activeSetting
    ? wholePeriodScore
      ? calculatePeriodSquadScore(
          playerResponses,
          playerMatchStats,
          activeSetting,
          { fromMs: null, toMs: null },
          events.length,
          !allSeasons && selectedSeason ? seasonReferenceNow(selectedSeason) : undefined,
        )
      : calculateMonthlySquadScoreDetailed(playerResponses, playerMatchStats, activeSetting, sessionsInCurrentMonth)
    : undefined;
  const latestScore = latestScoreBreakdown?.score;
  const latestAvgPerEvent = latestScoreBreakdown?.avgPerEvent;
  const seasonRows = seasons
    .map((season) => {
      const rows = allMatchStats
        .filter((stat) => stat.player_id === playerId && stat.season_id === season.id)
        .sort((a, b) => String(a.match_starts_at || "").localeCompare(String(b.match_starts_at || "")));
      return {
        season: season.name,
        status: season.status,
        matches: rows.length,
        starts: rows.filter((stat) => stat.started).length,
        goals: rows.reduce((acc, stat) => acc + (stat.goals || 0), 0),
        assists: rows.reduce((acc, stat) => acc + (stat.assists || 0), 0),
        scores: rows.map((stat) => (stat.goals || 0) + (stat.assists || 0) + (stat.started ? 1 : 0)),
      };
    })
    .filter((row) => row.matches > 0 || row.status === "active");

  // Sparkline form ultime 12 partite
  const formData = [...playerMatchStats]
    .sort((a, b) => String(a.match_starts_at || "").localeCompare(String(b.match_starts_at || "")))
    .slice(-12)
    .map((s) => (s.goals || 0) * 1 + (s.assists || 0) * 0.75 + (s.started ? 0.5 : 0));
  while (formData.length < 12) formData.unshift(0);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Testata: il numero di maglia e' l'identita' del giocatore. */}
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" className="-ml-3 text-foreground" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          {t("detail.back")}
        </Button>
        <div className="flex items-center gap-2">
            {canManage && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditOpen(true)}
                  title={t("detail.editTitle")}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {t("detail.edit")}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="h-9 w-9" title={t("detail.actions")} aria-label={t("detail.actions")}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {player.status !== "active" ? (
                      <DropdownMenuItem
                        onClick={() => setStatus.mutate({ id: player.id, status: "active" })}
                      >
                        {t("detail.reactivate")}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onClick={() => setStatus.mutate({ id: player.id, status: "inactive" })}
                      >
                        {t("detail.outOfSquad")}
                      </DropdownMenuItem>
                    )}
                    {player.status !== "archived" && (
                      <DropdownMenuItem
                        onClick={() => setStatus.mutate({ id: player.id, status: "archived" })}
                      >
                        {t("detail.archive")}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => {
                        if (
                          window.confirm(t("detail.deleteConfirm", { name: playerDisplayLabel }))
                        ) {
                          removePlayer.mutate(player.id);
                        }
                      }}
                    >
                      {t("detail.deletePlayer")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <PlayerEditDialog
                  open={editOpen}
                  onOpenChange={setEditOpen}
                  player={player}
                  teamId={teamId}
                />
              </>
            )}
        </div>
      </div>
      <header className="flex items-center gap-4 min-w-0">
        <span className={cn("eb-num text-[92px] pr-2 shrink-0", player.shirt_number == null && "text-[hsl(var(--faint))]")}>{player.shirt_number ?? "-"}</span>
        <div className="min-w-0">
          <h2 className="text-[26px] font-bold leading-tight break-words">{playerDisplayLabel}</h2>
          <p className="mt-0.5 text-muted-foreground">
            {player.is_guest ? t("detail.guest") : t("detail.player")} · {player.role_code || t("detail.roleNa")} · {player.ea_account_handle || player.ea_sports_id || t("detail.eaNa")}
          </p>
          <p className="mt-0.5 text-[13px] text-[hsl(var(--faint))] break-all">{player.email || linkedEmailQuery.data || t("detail.emailNa")}</p>
        </div>
      </header>

      {isOutOfSquad(player) && (
        <div className="rounded-[20px] bg-[hsl(var(--warn-soft))] px-5 py-4">
          <p className="font-semibold">
            {t("detail.outOfSquadBanner.title", { status: playerStatusLabel(player.status) })}
          </p>
          <p className="text-[13px] text-muted-foreground mt-0.5">{t("detail.outOfSquadBanner.hint")}</p>
        </div>
      )}

      {selectedSeason && (
        <SeasonScopeToggle seasonName={selectedSeason.name} allSeasons={allSeasons} onChange={setAllSeasons} />
      )}

      {/* Account collegato */}
      <LinkUserCard
        teamId={teamId}
        playerName={playerDisplayLabel}
        linkedEmail={linkedEmailQuery.data || null}
        canManage={canManage}
        onLink={(email) => linkUser.mutate({ id: player.id, email })}
        onUnlink={() => unlinkUser.mutate(player.id)}
        isLinking={linkUser.isPending}
        isUnlinking={unlinkUser.isPending}
      />

      <PlayerProfileTabs
        teamId={teamId}
        player={player}
        playerMatchStats={playerMatchStats}
        starts={starts}
        goals={goals}
        assists={assists}
        present={present}
        late={late}
        absent={absent}
        noResponse={noResponse}
        totalEvents={totalEvents}
        latestScore={latestScore}
        latestAvgPerEvent={latestAvgPerEvent}
        activeSetting={activeSetting}
        seasonRows={seasonRows}
        formData={formData}
        playerResponses={playerResponses}
        onOpenPlayer={openPlayer}
      />
    </div>
  );
}

function PlayerProfileTabs(props: {
  teamId: string;
  player: Player;
  playerMatchStats: MatchPlayerStat[];
  starts: number;
  goals: number;
  assists: number;
  present: number;
  late: number;
  absent: number;
  noResponse: number;
  totalEvents: number;
  latestScore: number | undefined;
  latestAvgPerEvent: number | undefined;
  activeSetting: SquadScoreSetting | null;
  seasonRows: any[];
  formData: number[];
  playerResponses: SessionResponse[];
  onOpenPlayer: (id: string) => void;
}) {
  const [tab, setTab] = useState<"overview" | "ea">("overview");
  // Tutti gli identificatori EA noti del giocatore: passiamoli entrambi a PlayerEaTab
  // perché EA salva nei match sia il gamertag (`name`/`playername`) sia il Pro Name
  // (`proName`), e non sappiamo a priori quale dei due l'utente ha popolato.
  const { t } = useTranslation("players");
  const eaCandidates = buildPlayerEaCandidates(props.player);
  return (
    <>
      <div className="grid grid-cols-2 sm:inline-grid w-full sm:w-auto rounded-full bg-raised p-1 text-sm">
        <button
          onClick={() => setTab("overview")}
          className={cn(
            "h-9 px-5 rounded-full font-medium transition-colors",
            tab === "overview" ? "bg-background text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {t("detail.tabOverview")}
        </button>
        <button
          onClick={() => setTab("ea")}
          className={cn(
            "h-9 px-5 rounded-full font-medium transition-colors flex items-center justify-center gap-1.5",
            tab === "ea" ? "bg-background text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <FcIcon className="h-3.5 w-3.5" />
          {t("detail.tabEa")}
        </button>
      </div>

      {tab === "overview" && (
        <PlayerOverviewTab
          {...props}
        />
      )}
      {tab === "ea" && (
        <PlayerEaTab
          teamId={props.teamId}
          eaCandidates={eaCandidates}
          playerId={props.player.id}
          displayName={props.player.display_name}
          roleCode={props.player.role_code}
          onOpenPlayer={props.onOpenPlayer}
        />
      )}
    </>
  );
}

function PlayerOverviewTab(props: {
  teamId: string;
  player: Player;
  playerMatchStats: MatchPlayerStat[];
  starts: number;
  goals: number;
  assists: number;
  present: number;
  late: number;
  absent: number;
  noResponse: number;
  totalEvents: number;
  latestScore: number | undefined;
  latestAvgPerEvent: number | undefined;
  activeSetting: SquadScoreSetting | null;
  seasonRows: any[];
  formData: number[];
  playerResponses: SessionResponse[];
}) {
  const { t } = useTranslation("players");
  const intlLocale = currentIntlLocale();
  const { teamId, player, playerMatchStats, starts, goals, assists, present, late, absent, noResponse, totalEvents, latestScore, latestAvgPerEvent, activeSetting, seasonRows, formData, playerResponses } = props;
  const pct = (n: number) => (totalEvents > 0 ? Math.round((n / totalEvents) * 1000) / 10 : 0);

  // Conteggio EA: distinguiamo season (league + playoff) da amichevoli.
  // Cerchiamo il record del player in `ea_match_snapshots` usando i due identificatori
  // possibili (gamertag + Pro Name) contro name/playername/proName del record EA.
  const eaCandidatesLower = [player.ea_sports_id, player.ea_account_handle]
    .map((v) => (v || "").trim().toLowerCase())
    .filter(Boolean);
  // Chiave stabile per usare l'array come dependency di useMemo/useQuery
  // senza falsi rerun causati dal cambio di reference del wrapping array.
  const eaCandidatesKey = eaCandidatesLower.join("|");
  const teamEaQuery = useQuery({
    queryKey: ["team-ea-club-id", teamId],
    queryFn: async () => {
      const { data } = await supabaseV2.from("teams").select("ea_club_id").eq("id", teamId).maybeSingle();
      return data?.ea_club_id as string | null;
    },
    enabled: eaCandidatesLower.length > 0,
  });
  const eaMatchesQuery = useQuery({
    queryKey: ["ea-matches-team", teamId, "overview"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("ea_match_snapshots")
        .select("ea_match_id, played_at, data")
        .eq("team_id", teamId)
        .order("played_at", { ascending: false, nullsFirst: false })
        .limit(2000);
      if (error) throw error;
      return (data || []) as Array<{ ea_match_id: string; played_at: string | null; data: any }>;
    },
    enabled: eaCandidatesLower.length > 0,
  });
  const eaCounts = useMemo(() => {
    const eaClubId = teamEaQuery.data;
    if (!eaClubId || eaCandidatesLower.length === 0) return { season: 0, friendly: 0 };
    let season = 0;
    let friendly = 0;
    for (const row of eaMatchesQuery.data || []) {
      const players = row.data?.players?.[ourClubIdOf(row.data, eaClubId)] || {};
      const arr = Object.values(players) as any[];
      const me = arr.find((p: any) => {
        const recordHandles = [p?.name, p?.playername, p?.proName]
          .filter(Boolean)
          .map((v: string) => String(v).toLowerCase());
        return recordHandles.some((h) => eaCandidatesLower.includes(h));
      }) as any;
      if (!me) continue;
      const seconds = Number(me.secondsPlayed || 0);
      if (seconds <= 0) continue;
      const t = String(row.data?.match_type || "");
      if (t === "leagueMatch" || t === "playoffMatch") season += 1;
      else if (t === "friendlyMatch") friendly += 1;
    }
    return { season, friendly };
    // `eaCandidatesKey` stabilizza il reference (eaCandidatesLower cambia
    // identita' a ogni render). ESLint vorrebbe l'array crudo, ma e' proprio
    // cio' che vogliamo evitare.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamEaQuery.data, eaMatchesQuery.data, eaCandidatesKey]);

  // Carriera club aggregata: legge `ea_member_snapshots` (gamesPlayed che EA fornisce
  // per il membro del club). E' il "100" che si vede in Mondo FC > Membri.
  // Provo i candidates in ordine: il primo che torna dato vince.
  const eaMemberQuery = useQuery({
    queryKey: ["ea-member", teamId, eaCandidatesLower.join("|"), "overview"],
    queryFn: async () => {
      for (const handle of [player.ea_sports_id, player.ea_account_handle]) {
        const h = (handle || "").trim();
        if (!h) continue;
        const { data, error } = await supabaseV2.rpc("latest_ea_member", {
          _team_id: teamId,
          _ea_persona: h,
        });
        if (error) continue;
        if (data) return data as any;
      }
      return null;
    },
    enabled: eaCandidatesLower.length > 0,
  });
  const eaCareer = (() => {
    const m = eaMemberQuery.data;
    if (!m) return null;
    const games = Number(m.gamesPlayed ?? m.gameplays ?? 0);
    const goals = Number(m.goals ?? 0);
    const assists = Number(m.assists ?? 0);
    return games > 0 ? { games, goals, assists } : null;
  })();
  return (
    <div className="space-y-4">
      {/* KPI strip + sparkline form */}
      <Card className="shadow-card hud-card">
        <CardContent className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-x-4 gap-y-5 items-start">
          <div className="flex flex-col gap-0.5">
            <span className="stat-label">{t("detail.kpi.matchDay")}</span>
            <span className="stat-value">{playerMatchStats.length}</span>
            <span className="stat-hint">{starts > 0 ? t("detail.kpi.matchDayHintStarts", { count: starts }) : t("detail.kpi.matchDayHintFallback")}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="stat-label flex items-center gap-1.5">
              <FcIcon className="h-3.5 w-3.5 shrink-0" />
              {t("detail.kpi.eaCareer")}
            </span>
            <span className="stat-value">{eaCareer?.games ?? "-"}</span>
            <span className="stat-hint">{t("detail.kpi.eaCareerHint")}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="stat-label flex items-center gap-1.5">
              <FcIcon className="h-3.5 w-3.5 shrink-0" />
              {t("detail.kpi.eaDownloaded")}
            </span>
            <span className="stat-value">{eaCounts.season + eaCounts.friendly}</span>
            <span className="stat-hint">
              {t("detail.kpi.eaDownloadedHint", { season: eaCounts.season, friendly: eaCounts.friendly })}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="stat-label">{t("detail.kpi.goals")}</span>
            <span className="stat-value">{goals}</span>
            <span className="stat-hint">
              {playerMatchStats.length > 0 ? (goals / playerMatchStats.length).toFixed(2) : "0"} {t("detail.kpi.perMatch")}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="stat-label">{t("detail.kpi.assists")}</span>
            <span className="stat-value">{assists}</span>
            <span className="stat-hint">
              {playerMatchStats.length > 0 ? (assists / playerMatchStats.length).toFixed(2) : "0"} {t("detail.kpi.perMatch")}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="stat-label">{t("detail.kpi.presences")}</span>
            <span className="stat-value">{present + late}</span>
            <span className="stat-hint">
              {present + late + absent > 0 ? t("detail.kpi.presenceRate", { pct: Math.round(((present + late) / (present + late + absent)) * 1000) / 10 }) : "-"}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="stat-label">{t("detail.kpi.squadScore")}</span>
            <span className="stat-value is-accent">
              {latestScore !== undefined ? Number(latestScore).toFixed(1) : "-"}
            </span>
            <span className="stat-hint">
              {latestAvgPerEvent !== undefined && totalEvents > 0
                ? t("detail.kpi.squadScoreAvg", { value: latestAvgPerEvent.toFixed(2) })
                : activeSetting
                  ? t("detail.kpi.presetActive", { name: activeSetting.name })
                  : t("detail.kpi.presetNone")}
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="stat-label">{t("detail.kpi.form")}</span>
            <Sparkline values={formData} color="hsl(var(--accent))" />
          </div>
        </CardContent>
      </Card>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Risposte presenza con cells colorate + stack bar */}
        <Card className="shadow-card lg:col-span-5">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-lg">{t("detail.responses.title")}</CardTitle>
            <span className="text-xs text-muted-foreground">{t("detail.responses.totalEvents", { count: totalEvents })}</span>
          </CardHeader>
          <CardContent className="space-y-3">
            {totalEvents > 0 ? (
              <div className="bar-stack h-2.5 rounded-[5px] bg-transparent">
                {([[present, "bg-[hsl(var(--ok))]"], [late, "bg-[hsl(var(--warn))]"], [absent, "bg-[hsl(var(--danger))]"], [noResponse, "bg-[hsl(var(--input))]"]] as const).map(([value, tone]) =>
                  value > 0 ? <div key={tone} className={cn("h-full rounded-[2px]", tone)} style={{ flexGrow: value }} /> : null,
                )}
              </div>
            ) : (
              <div className="bar-stack h-2.5 rounded-[5px]" />
            )}
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {([["ok", present, "present"], ["warn", late, "late"], ["danger", absent, "absent"], ["muted", noResponse, "noResponse"]] as const).map(([tone, value, key]) => (
                <div key={key} className="flex items-center gap-2 min-w-0">
                  <span aria-hidden className={`eb-dot dot-${tone}`} />
                  <dt className="text-muted-foreground truncate">{t(`detail.responses.${key}`)}</dt>
                  <dd className="ml-auto tabular-nums whitespace-nowrap">{value} <span className="text-[hsl(var(--faint))]">· {pct(value)}%</span></dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>

        {/* Stagioni con sparkline */}
        <Card className="shadow-card lg:col-span-7">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t("detail.seasons.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="text-left eb-data text-[13px] py-2 pr-2">{t("detail.seasons.header")}</th>
                  <th className="text-right eb-data text-[13px] px-1">{t("detail.seasons.p")}</th>
                  <th className="text-right eb-data text-[13px] px-1">{t("detail.seasons.t")}</th>
                  <th className="text-right eb-data text-[13px] px-1">{t("detail.seasons.g")}</th>
                  <th className="text-right eb-data text-[13px] px-1">{t("detail.seasons.a")}</th>
                  <th className="text-left eb-data text-[13px] pl-3">{t("detail.seasons.form")}</th>
                </tr>
              </thead>
              <tbody>
                {seasonRows.map((row) => (
                  <tr key={row.season} className="border-t">
                    <td className="py-2 pr-2">
                      <p className="font-semibold">{row.season}</p>
                      <p className="text-xs text-muted-foreground">{row.status === "active" ? t("detail.seasons.current") : t("detail.seasons.ended")}</p>
                    </td>
                    <td className="text-right font-tabular px-1">{row.matches}</td>
                    <td className="text-right font-tabular px-1">{row.starts}</td>
                    <td className="text-right font-tabular px-1">{row.goals}</td>
                    <td className="text-right font-tabular px-1">{row.assists}</td>
                    <td className="pl-3">
                      <Sparkline
                        values={row.scores.slice(-12)}
                        color={row.status === "active" ? "hsl(var(--accent))" : "hsl(var(--muted-foreground))"}
                      />
                    </td>
                  </tr>
                ))}
                {seasonRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-xs text-muted-foreground">
                      {t("detail.seasons.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Ultime partite con score chip */}
        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t("detail.recentMatches.title")}</CardTitle>
          </CardHeader>
          <CardContent className="max-h-[26rem] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground sticky top-0 bg-card">
                <tr>
                  <th className="text-left eb-data text-[13px] py-2 pr-2">{t("detail.recentMatches.opponent")}</th>
                  <th className="text-left eb-data text-[13px] px-1">{t("detail.recentMatches.date")}</th>
                  <th className="text-right eb-data text-[13px] px-1">{t("detail.recentMatches.result")}</th>
                  <th className="text-right eb-data text-[13px] px-1">{t("detail.recentMatches.g")}</th>
                  <th className="text-right eb-data text-[13px] px-1">{t("detail.recentMatches.a")}</th>
                  <th className="eb-data text-[13px] px-1">{t("detail.recentMatches.role")}</th>
                </tr>
              </thead>
              <tbody>
                {playerMatchStats.slice(0, 20).map((stat) => {
                  const us = stat.match_our_score;
                  const them = stat.match_opponent_score;
                  const outcome =
                    us != null && them != null ? (us > them ? "win" : us === them ? "draw" : "lose") : null;
                  return (
                    <tr key={stat.id} className="border-t hover:bg-raised">
                      <td className="py-2 pr-2 font-medium">{stat.match_opponent_name || t("detail.recentMatches.fallback")}</td>
                      <td className="text-xs text-muted-foreground px-1">
                        {stat.match_starts_at
                          ? new Date(stat.match_starts_at).toLocaleDateString(intlLocale, { day: "2-digit", month: "short" })
                          : "-"}
                      </td>
                      <td className="text-right px-1">
                        {outcome ? (
                          <span className={cn("score-chip", outcome)}>{us}-{them}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="text-right font-tabular px-1">{stat.goals || 0}</td>
                      <td className="text-right font-tabular px-1">{stat.assists || 0}</td>
                      <td className="px-1">
                        <span className="tag-mono">{stat.started ? t("detail.recentMatches.starter") : t("detail.recentMatches.sub")}</span>
                      </td>
                    </tr>
                  );
                })}
                {playerMatchStats.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-xs text-muted-foreground">
                      {t("detail.recentMatches.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Ultime risposte */}
        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t("detail.recentResponses.title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 max-h-[26rem] overflow-y-auto">
            {playerResponses.slice(0, 30).map((response) => (
              <div
                key={response.id}
                className="grid grid-cols-[60px_1fr_auto] items-center gap-3 py-1.5 border-b border-border/40 last:border-b-0"
              >
                <span className="eb-data text-[13px] text-[hsl(var(--faint))]">
                  {response.session_starts_at
                    ? new Date(response.session_starts_at).toLocaleDateString(intlLocale, { day: "2-digit", month: "short" })
                    : "-"}
                </span>
                <span className="text-sm truncate">{response.session_title || t("detail.recentResponses.fallback")}</span>
                <Badge className={statusBadgeClass[response.status]}>{statusLabel(response.status)}</Badge>
              </div>
            ))}
            {playerResponses.length === 0 && <EmptyState text={t("detail.responses.empty")} />}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
